import { capitalize } from 'lodash';
import { Addon } from '~api/som';

import { ReactNode, useMemo, useState } from 'react';

import { Button, Checkbox, CheckboxLayout, ListLayout, Modal } from '@ingrid/luterkort-react';

import { useTranslation } from '~src/i18n/use-translation';

import { useCarrierAddonsQuery } from '../queries';
import { useShipmentFormContext } from '../types';

interface Props {
  trigger: ({ disabled }: { disabled: boolean }) => ReactNode;
  onSubmit: (values?: Addon[]) => void;
  value?: Addon[];
}

/**
 * The title consists of the addon code, along with a modified version of the addon name.
 *
 * The name might, or might not be capitalized, therefore we need to normalize that.
 * The name might, or might not include underscores, therefore we need to normalize that.
 *
 * The code or name might also be either null, or undefined, therefore we need to filter them out.
 * Once normalized, we join them togher with " - "
 *
 * ["XX", "name_here"] => "XX - Name Here"
 */
export function formatAddonTitle(addon: Addon): string {
  return [addon.code, addon.name?.split(' ').map(n => capitalize(n).replace(/_/g, ' '))]
    .filter(part => !!part)
    .join(' - ');
}

export function CarrierProductAddons(props: Props) {
  const { onSubmit, value = [], trigger } = props;

  const [selectedAddons, setSelectedAddons] = useState(value);
  const [modalOpen, setModalOpen] = useState(false);

  const { watch } = useShipmentFormContext();
  const { data } = useCarrierAddonsQuery();
  const { t } = useTranslation();

  const selectedShippingMethodId = watch('shipping_method.id');

  const availableAddonsByShippingMethod = useMemo(() => {
    return (
      data?.addons?.filter(a => selectedShippingMethodId && a.shipping_methods?.includes(selectedShippingMethodId)) ??
      []
    );
  }, [data?.addons, selectedShippingMethodId]);

  const availableAddonsByShippingMethodGrouped = useMemo(() => {
    const grouped =
      data?.categories?.map(category => ({
        addons:
          availableAddonsByShippingMethod.filter(addon => addon.type && category.addon_types?.includes(addon.type)) ??
          [],
        category,
      })) ?? [];

    return grouped.filter(group => !!group.addons?.length);
  }, [data?.categories, availableAddonsByShippingMethod]);

  function handleSubmit() {
    onSubmit(selectedAddons);
    setModalOpen(false);
  }

  return (
    <Modal.Root
      open={modalOpen}
      onOpenChange={o => {
        setSelectedAddons(value);
        setModalOpen(o);
      }}
    >
      <Modal.Trigger>{trigger({ disabled: !availableAddonsByShippingMethod.length })}</Modal.Trigger>

      <Modal.Content>
        <Modal.Header title={t('common.addAddons')} />

        <Modal.Body css={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <ListLayout.Column style={{ rowGap: '24px' }}>
            {availableAddonsByShippingMethodGrouped.map(group => (
              <ListLayout.Stack key={group.category.type} style={{ rowGap: '16px' }}>
                <ListLayout.Column style={{ rowGap: '4px' }}>
                  <ListLayout.Text style={{ fontWeight: 'bold' }}>
                    {t(`carrier.addon.type.${group.category.type!}.title`)}
                  </ListLayout.Text>
                  <ListLayout.Text>{t(`carrier.addon.type.${group.category.type!}.description`)}</ListLayout.Text>
                </ListLayout.Column>

                <ListLayout.Row>
                  <ListLayout.Stack style={{ rowGap: '16px' }}>
                    {group.addons.map((addon, i) => (
                      <CheckboxLayout.Row key={addon.code}>
                        <Checkbox
                          checked={selectedAddons?.map(a => a.code).includes(addon.code)}
                          id={addon.code}
                          onCheckedChange={checked =>
                            setSelectedAddons(prev =>
                              checked
                                ? [...prev.slice(0, i), addon, ...prev.slice(i)]
                                : prev.filter(a => a.code !== addon.code)
                            )
                          }
                        />
                        <CheckboxLayout.Text as="label" htmlFor={addon.code}>
                          {formatAddonTitle(addon)}
                        </CheckboxLayout.Text>
                      </CheckboxLayout.Row>
                    ))}
                  </ListLayout.Stack>
                </ListLayout.Row>
              </ListLayout.Stack>
            ))}
          </ListLayout.Column>
        </Modal.Body>

        <Modal.Footer>
          <Modal.CloseButton>
            <Button variant="secondary">{t('common.cancel')}</Button>
          </Modal.CloseButton>

          <Button type="submit" onClick={handleSubmit}>
            {t('common.save')}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
